<script>
import CandidateImage from '@/components/candidate/candidate-image.vue'
import baseMixin from '@/mixins/baseMixin'
import NoEducator from './no-educator.vue'
import CustomTooltip from './custom-tooltip.vue'

const MAX_VISIBLE_CANDIDATE_AVATARS = 3

export default {
  name: 'BlockEducator',
  components: {
    CustomTooltip,
    CandidateImage,
    NoEducator,
  },
  mixins: [baseMixin],
  props: {
    classificationName: { type: String, default: '' },
    uniqueBlockCandidates: { type: Array, default: () => [] },
  },
  emits: ['viewCandidate'],
  computed: {
    numberOfCandidatesTruncated() {
      const numTruncatedCandidates =
        this.uniqueBlockCandidates.length - MAX_VISIBLE_CANDIDATE_AVATARS
      return numTruncatedCandidates || 0
    },
    showTruncatedCandidateCount() {
      return this.uniqueBlockCandidates.length > MAX_VISIBLE_CANDIDATE_AVATARS
    },
  },
  methods: {
    candidateFullName(candidate) {
      if (!candidate) return ''
      return `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim()
    },
    isNotLastVisibleCandidateAvatar(index) {
      const hasMultipleCandidatesInBooking =
        this.uniqueBlockCandidates.length > 1
      const isNotLast = index !== MAX_VISIBLE_CANDIDATE_AVATARS - 1 // -1 for index offset

      return hasMultipleCandidatesInBooking && isNotLast
    },
  },
}
</script>

<template>
  <div class="d-flex align-center">
    <template v-if="uniqueBlockCandidates.length">
      <template v-if="isMobile">
        <div
          v-for="candidate in uniqueBlockCandidates.slice(0, 3)"
          :key="`candidate_avatar_${candidate.id}`"
          class="educator-avatar"
          :class="[uniqueBlockCandidates.length > 1 ? 'me-2' : 'me-4']"
        >
          <CandidateImage
            :id="candidate.id"
            :first-name="candidate.firstName"
            :last-name="candidate.lastName"
            size="sm"
            @click="$emit('viewCandidate', candidate)"
          />
        </div>
      </template>
      <template v-else>
        <div
          v-for="(candidate, index) in uniqueBlockCandidates.slice(0, 3)"
          :key="`educator_avatar_${candidate.id}`"
          class="educator-avatar"
          :class="{
            'mr-6': isNotLastVisibleCandidateAvatar(index),
          }"
        >
          <CustomTooltip
            position="right"
            :text="`${candidate.firstName} ${candidate.lastName}`"
          >
            <CandidateImage
              :id="candidate.id"
              :first-name="candidate.firstName"
              :last-name="candidate.lastName"
              size="sm"
              @click="$emit('viewCandidate', candidate)"
            />
          </CustomTooltip>
        </div>
      </template>

      <span v-if="showTruncatedCandidateCount" class="ms-1"
        >+ {{ numberOfCandidatesTruncated }}</span
      >

      <template v-if="uniqueBlockCandidates.length === 1">
        <div
          v-if="isMobile"
          class="d-flex flex-column text-start"
          style="margin-left: -8px"
        >
          <span
            v-for="educator in uniqueBlockCandidates.slice(0, 3)"
            :key="`candidate_name_${educator.id}`"
            class="educator-name pl-0"
            @click="educator && $emit('viewCandidate', educator)"
          >
            {{ candidateFullName(educator) }}
          </span>
          <span> {{ classificationName }}</span>
        </div>
        <template v-else>
          <span
            v-for="educator in uniqueBlockCandidates.slice(0, 3)"
            :key="`educator_name_${educator.id}`"
            class="educator-name"
            @click="educator && $emit('viewCandidate', educator)"
          >
            {{ candidateFullName(educator) }}
          </span>
        </template>
      </template>
    </template>

    <template v-else>
      <NoEducator :classification-name="isMobile ? classificationName : ''" />
    </template>
    <span v-if="isMobile && uniqueBlockCandidates.length > 1">
      {{ classificationName }}</span
    >
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';

.candidateImage {
  margin-right: 0 !important;
}

.educator-avatar {
  position: relative;
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  transition: transform 0.12s ease;
}

.educator-avatar + .educator-avatar {
  margin-left: -30px;
}

.educator-avatar:hover {
  z-index: 5;
  transform: translateY(-1px);
}

.educator-name {
  max-width: 250px;
  padding-left: 8px;
  overflow: hidden;
  font-weight: 500;
  line-height: normal;
  color: $color-gray-600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
